import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { adminTheme } from '../styles/adminTheme';

const TableWrapper = styled.div`
  background: ${adminTheme.colors.cardBg};
  border: 1px solid ${adminTheme.colors.border};
  border-radius: ${adminTheme.borderRadius};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th<{ $sortable?: boolean }>`
  text-align: left;
  padding: 10px 14px;
  font-size: 11px;
  font-weight: 600;
  color: ${adminTheme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  border-bottom: 2px solid ${adminTheme.colors.border};
  background: #fafbfc;
  cursor: ${p => p.$sortable ? 'pointer' : 'default'};
  white-space: nowrap;
  user-select: none;

  &:hover {
    ${p => p.$sortable && `color: ${adminTheme.colors.text};`}
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${adminTheme.colors.text};
  border-bottom: 1px solid ${adminTheme.colors.border};
`;

const Tr = styled.tr<{ $clickable?: boolean }>`
  cursor: ${p => p.$clickable ? 'pointer' : 'default'};

  &:nth-child(even) {
    background: ${adminTheme.colors.rowAlt};
  }

  &:hover {
    ${p => p.$clickable && `background: #e8f0fe20;`}
  }
`;

const EmptyRow = styled.tr`
  td {
    padding: 32px 14px;
    text-align: center;
    color: ${adminTheme.colors.textMuted};
    font-size: 13px;
  }
`;

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 14px;
  font-size: 12px;
  color: ${adminTheme.colors.textSecondary};
  border-top: 1px solid ${adminTheme.colors.border};
`;

const PageButton = styled.button<{ $active?: boolean }>`
  padding: 4px 10px;
  border: 1px solid ${p => p.$active ? adminTheme.colors.primary : adminTheme.colors.border};
  border-radius: 4px;
  background: ${p => p.$active ? adminTheme.colors.primary : 'white'};
  color: ${p => p.$active ? 'white' : adminTheme.colors.text};
  font-size: 12px;
  cursor: pointer;
  margin: 0 2px;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: ${p => p.$active ? adminTheme.colors.primaryHover : '#f8f9fa'};
  }
`;

const LoadingOverlay = styled.div`
  padding: 40px;
  text-align: center;
  color: ${adminTheme.colors.textMuted};
  font-size: 13px;
`;

export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  render?: (row: T) => React.ReactNode;
  width?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  loading?: boolean;
  pageSize?: number;
  emptyMessage?: string;
}

export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  onRowClick,
  loading,
  pageSize = 15,
  emptyMessage = 'No data found',
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);

  const sorted = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paginated = sorted.slice(page * pageSize, (page + 1) * pageSize);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  if (loading) {
    return <TableWrapper><LoadingOverlay>Loading...</LoadingOverlay></TableWrapper>;
  }

  return (
    <TableWrapper>
      <Table>
        <thead>
          <tr>
            {columns.map(col => (
              <Th
                key={col.key}
                $sortable={col.sortable}
                onClick={() => col.sortable && handleSort(col.key)}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.header}
                {sortKey === col.key && (sortDir === 'asc' ? ' ▲' : ' ▼')}
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {paginated.length === 0 ? (
            <EmptyRow>
              <td colSpan={columns.length}>{emptyMessage}</td>
            </EmptyRow>
          ) : (
            paginated.map((row, i) => (
              <Tr
                key={row.id ?? i}
                $clickable={!!onRowClick}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map(col => (
                  <Td key={col.key}>
                    {col.render ? col.render(row) : String(row[col.key] ?? '')}
                  </Td>
                ))}
              </Tr>
            ))
          )}
        </tbody>
      </Table>
      {totalPages > 1 && (
        <PaginationBar>
          <span>
            Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, sorted.length)} of {sorted.length}
          </span>
          <div>
            <PageButton disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</PageButton>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const pageIdx = Math.max(0, Math.min(page - 2, totalPages - 5)) + i;
              if (pageIdx >= totalPages) return null;
              return (
                <PageButton key={pageIdx} $active={pageIdx === page} onClick={() => setPage(pageIdx)}>
                  {pageIdx + 1}
                </PageButton>
              );
            })}
            <PageButton disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</PageButton>
          </div>
        </PaginationBar>
      )}
    </TableWrapper>
  );
}
